import React, { useEffect, useState } from "react";
import { Loader2, Pencil, UserRound, BellRing } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { PushToggle } from "../components/PushToggle";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";

const EDITABLE = [
  ["name", "Nombre completo"], ["email", "Correo"], ["phone", "Teléfono"],
  ["birthdate", "Fecha de nacimiento", "date"], ["blood_type", "Tipo de sangre"],
  ["address", "Dirección"], ["emergency_contact", "Contacto de emergencia"],
  ["emergency_phone", "Teléfono de emergencia"], ["photo_url", "URL de fotografía"],
];

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-border/60">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right">{value || "—"}</span>
    </div>
  );
}

export default function Profile() {
  const { user } = useAuth();
  const canEdit = !!user && (user.is_admin || user.role_name === "Dirección General");
  const [p, setP] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [pushCats, setPushCats] = useState([]);
  const [pushPrefs, setPushPrefs] = useState({});
  const [dnd, setDnd] = useState({ enabled: false, start: "22:00", end: "07:00" });

  const load = () => api.get("/profile").then((r) => setP(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/push/preferences")
      .then((r) => { setPushCats(r.data.categories || []); setPushPrefs(r.data.prefs || {}); if (r.data.dnd) setDnd(r.data.dnd); })
      .catch(() => {});
  }, []);

  const saveDnd = async (next) => {
    setDnd(next);
    try { await api.put("/push/dnd", next); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const togglePref = async (key, value) => {
    const next = { ...pushPrefs, [key]: value };
    setPushPrefs(next);
    try { await api.put("/push/preferences", { prefs: next }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPushPrefs(pushPrefs); }
  };

  const [testingPush, setTestingPush] = useState(false);
  const sendTestPush = async () => {
    setTestingPush(true);
    try {
      const { data } = await api.post("/push/test");
      toast.success(`Notificación de prueba enviada a ${data.devices} dispositivo(s). Revísala en unos segundos.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setTestingPush(false); }
  };

  const openEdit = () => {
    const f = {}; EDITABLE.forEach(([k]) => (f[k] = p[k] || "")); setForm(f); setOpen(true);
  };
  const save = async () => {
    setSaving(true);
    try { await api.put("/profile", form); toast.success("Perfil actualizado"); setOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!p) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 max-w-4xl">
      <Card className="rounded-sm border shadow-none p-6 flex flex-col sm:flex-row items-center gap-6">
        <div className="h-24 w-24 rounded-sm bg-muted overflow-hidden flex items-center justify-center shrink-0">
          {p.photo_url ? <img src={p.photo_url} alt="" className="h-full w-full object-cover" /> : <UserRound className="h-12 w-12 text-muted-foreground" />}
        </div>
        <div className="flex-1 text-center sm:text-left">
          <h2 className="font-heading text-2xl font-black">{p.name}</h2>
          <p className="text-sm text-muted-foreground font-mono">@{p.username}</p>
          <div className="mt-2 flex flex-wrap gap-2 justify-center sm:justify-start">
            <span className="text-xs bg-primary/15 text-primary px-2 py-0.5 rounded-sm font-bold uppercase">{p.role_name}</span>
            {p.position && <span className="text-xs bg-muted px-2 py-0.5 rounded-sm">{p.position}</span>}
            <span className="text-xs bg-muted px-2 py-0.5 rounded-sm">{p.employment_status}</span>
          </div>
        </div>
        {canEdit
          ? <Button onClick={openEdit} className="rounded-sm gap-2" data-testid="edit-profile-btn"><Pencil className="h-4 w-4" /> Editar</Button>
          : <span className="text-[10px] text-muted-foreground text-center sm:text-right max-w-[140px]">Solo la Dirección General puede editar perfiles.</span>}
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="rounded-sm border shadow-none p-5">
          <h3 className="font-heading font-bold mb-3">Datos personales</h3>
          <Row label="Correo" value={p.email} />
          <Row label="Teléfono" value={p.phone} />
          <Row label="Nacimiento" value={p.birthdate} />
          <Row label="Edad" value={p.age ? `${p.age} años` : ""} />
          <Row label="Tipo de sangre" value={p.blood_type} />
          <Row label="Dirección" value={p.address} />
        </Card>
        <Card className="rounded-sm border shadow-none p-5">
          <h3 className="font-heading font-bold mb-3">Laboral y emergencia</h3>
          <Row label="Puesto" value={p.position} />
          <Row label="Departamento" value={p.department} />
          <Row label="Jefe inmediato" value={p.boss} />
          <Row label="Ingreso" value={p.hire_date} />
          <Row label="Días de vacaciones" value={String(p.vacation_days)} />
          <Row label="Contacto emergencia" value={p.emergency_contact} />
          <Row label="Tel. emergencia" value={p.emergency_phone} />
        </Card>
      </div>

      <Card className="rounded-sm border shadow-none p-5" data-testid="push-preferences-card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="font-heading font-bold">Notificaciones push</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Elige qué eventos te avisan en este y otros dispositivos. Las notificaciones dentro de la app no se ven afectadas.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="gap-2" onClick={sendTestPush} disabled={testingPush} data-testid="send-test-push-btn">
              {testingPush ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />}
              <span className="hidden sm:inline">Probar</span>
            </Button>
            <PushToggle />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
          {pushCats.map((c) => (
            <label key={c.key} className="flex items-center justify-between gap-3 py-2 border-b border-border/60 cursor-pointer" data-testid={`push-pref-${c.key}`}>
              <span className="text-sm">{c.label}</span>
              <Switch checked={pushPrefs[c.key] !== false} onCheckedChange={(v) => togglePref(c.key, v)} data-testid={`push-pref-switch-${c.key}`} />
            </label>
          ))}
        </div>
        <div className="mt-5 pt-4 border-t border-border" data-testid="push-dnd-section">
          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <div>
              <span className="text-sm font-semibold">Modo no molestar</span>
              <p className="text-[11px] text-muted-foreground">No recibir notificaciones push dentro de este horario (hora de Guatemala). Las notificaciones dentro de la app se siguen guardando.</p>
            </div>
            <Switch checked={!!dnd.enabled} onCheckedChange={(v) => saveDnd({ ...dnd, enabled: v })} data-testid="push-dnd-switch" />
          </label>
          {dnd.enabled && (
            <>
              <div className="flex items-center gap-3 mt-3">
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Desde</Label>
                  <Input type="time" value={dnd.start} onChange={(e) => saveDnd({ ...dnd, start: e.target.value })} className="rounded-sm w-32" data-testid="push-dnd-start" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Hasta</Label>
                  <Input type="time" value={dnd.end} onChange={(e) => saveDnd({ ...dnd, end: e.target.value })} className="rounded-sm w-32" data-testid="push-dnd-end" />
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Días completos sin push</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="push-dnd-days">
                  {[["Lun", 0], ["Mar", 1], ["Mié", 2], ["Jue", 3], ["Vie", 4], ["Sáb", 5], ["Dom", 6]].map(([lbl, d]) => {
                    const on = (dnd.days || []).includes(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        data-testid={`push-dnd-day-${d}`}
                        onClick={() => { const s = new Set(dnd.days || []); on ? s.delete(d) : s.add(d); saveDnd({ ...dnd, days: [...s].sort((a, b) => a - b) }); }}
                        className={`px-2.5 py-1 rounded-sm text-xs font-semibold border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:bg-muted"}`}
                      >
                        {lbl}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto" data-testid="profile-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Editar mi perfil</DialogTitle>
            <DialogDescription>Solo puedes editar tus datos personales. El rol, puesto y departamento los define un administrador.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {EDITABLE.map(([k, label, type]) => (
              <div className="space-y-1.5" key={k}>
                <Label className="text-xs font-bold uppercase tracking-widest">{label}</Label>
                <Input type={type || "text"} className="rounded-sm" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`profile-${k}`} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="rounded-sm font-bold" onClick={save} disabled={saving} data-testid="save-profile-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
